import { z } from "zod";
import {
  CURRENCIES,
  INVOICE_KINDS,
  INVOICE_STATUSES,
  PAYMENT_METHODS,
  PAYMENT_STATUSES,
  QR_REFERENCE_TYPES,
  type InvoiceKind,
  type InvoiceStatus,
  type PaymentStatus,
} from "../status.js";
import {
  documentDiscountSchema,
  lineItemInputSchema,
  type CommercialLineResponse,
  type CommercialTotalsResponse,
  type CustomerRef,
  type DocumentRef,
} from "./offers.js";

const moneyInput = z
  .union([z.number().finite(), z.string().regex(/^\d{1,12}(\.\d{1,2})?$/)])
  .transform(String);
const optionalLongText = z.string().trim().max(4000).nullable().optional();

export const billingSnapshotSchema = z.object({
  name: z.string().trim().min(1).max(160),
  company: z.string().trim().max(160).nullable().optional(),
  line1: z.string().trim().min(1).max(120),
  line2: z.string().trim().max(120).nullable().optional(),
  postalCode: z.string().trim().min(1).max(12),
  city: z.string().trim().min(1).max(80),
  country: z.string().trim().length(2).default("CH"),
});
export type BillingSnapshot = z.infer<typeof billingSnapshotSchema>;

/** @deprecated use lineItemInputSchema — kept as an alias for clarity. */
export const invoiceLineItemInputSchema = lineItemInputSchema;

/**
 * Create a draft invoice. Exactly one source may be given:
 * - `offerId`: bill an accepted offer (lines default to everything not yet
 *   invoiced);
 * - `requestId`: bill a request directly (e.g. a fixed-price job);
 * - `customerId`: a free invoice.
 */
export const invoiceCreateSchema = z
  .object({
    customerId: z.string().uuid().optional(),
    requestId: z.string().uuid().optional(),
    offerId: z.string().uuid().optional(),
    projectId: z.string().uuid().nullable().optional(),
    billing: billingSnapshotSchema.optional(),
    currency: z.enum(CURRENCIES).optional(),
    title: z.string().trim().max(180).nullable().optional(),
    introText: optionalLongText,
    paymentTermsText: optionalLongText,
    paymentTermDays: z.number().int().min(0).max(365).optional(),
    referenceType: z.enum(QR_REFERENCE_TYPES).optional(),
    items: z.array(lineItemInputSchema).max(500).optional(),
  })
  .merge(documentDiscountSchema)
  .superRefine((value, ctx) => {
    const sources = [value.customerId, value.offerId, value.requestId].filter(
      Boolean,
    ).length;
    if (sources === 0) {
      ctx.addIssue({
        code: "custom",
        path: ["customerId"],
        message: "Customer, request or accepted offer is required",
      });
    }
    if (value.offerId && value.requestId) {
      ctx.addIssue({
        code: "custom",
        path: ["requestId"],
        message: "Choose either an offer or a request as the source",
      });
    }
  });
export type InvoiceCreateDto = z.infer<typeof invoiceCreateSchema>;

export const invoiceUpdateSchema = z
  .object({
    version: z.number().int().min(1),
    billing: billingSnapshotSchema.optional(),
    currency: z.enum(CURRENCIES).optional(),
    title: z.string().trim().max(180).nullable().optional(),
    introText: optionalLongText,
    paymentTermsText: optionalLongText,
    paymentTermDays: z.number().int().min(0).max(365).optional(),
    referenceType: z.enum(QR_REFERENCE_TYPES).optional(),
    items: z.array(lineItemInputSchema).max(500).optional(),
  })
  .merge(documentDiscountSchema);
export type InvoiceUpdateDto = z.infer<typeof invoiceUpdateSchema>;

export const invoiceActionSchema = z.object({
  version: z.number().int().min(1),
});
export type InvoiceActionDto = z.infer<typeof invoiceActionSchema>;

export const invoiceCancelSchema = z.object({
  version: z.number().int().min(1),
  reason: z.string().trim().min(1).max(1000),
});
export type InvoiceCancelDto = z.infer<typeof invoiceCancelSchema>;

/** Create a draft credit note for an issued invoice. */
export const creditNoteCreateSchema = z.object({
  reason: z.string().trim().min(1).max(1000),
  /** Defaults to crediting the full open amount as one line per tax rate. */
  items: z.array(lineItemInputSchema).min(1).max(500).optional(),
});
export type CreditNoteCreateDto = z.infer<typeof creditNoteCreateSchema>;

export const invoiceListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
  q: z.string().trim().max(120).optional(),
  status: z.enum(INVOICE_STATUSES).optional(),
  kind: z.enum(INVOICE_KINDS).optional(),
  paymentStatus: z.enum(PAYMENT_STATUSES).optional(),
  overdue: z
    .enum(["true", "false"])
    .transform((v) => v === "true")
    .optional(),
  /** Issued/sent invoices with an open balance (unpaid or partly paid). */
  open: z
    .enum(["true", "false"])
    .transform((v) => v === "true")
    .optional(),
  customerId: z.string().uuid().optional(),
  offerId: z.string().uuid().optional(),
  requestId: z.string().uuid().optional(),
  projectId: z.string().uuid().optional(),
  sortField: z
    .enum(["number", "invoiceDate", "dueDate", "grandTotal", "createdAt"])
    .default("createdAt"),
  sortOrder: z.enum(["asc", "desc"]).default("desc"),
});
export type InvoiceListQuery = z.infer<typeof invoiceListQuerySchema>;

export const paymentCreateSchema = z.object({
  amount: moneyInput.refine(
    (value) => Number(value) > 0,
    "Amount must be greater than zero",
  ),
  paymentDate: z.string().date(),
  method: z.enum(PAYMENT_METHODS).default("bank_transfer"),
  reference: z.string().trim().max(120).nullable().optional(),
  note: z.string().trim().max(1000).nullable().optional(),
});
export type PaymentCreateDto = z.infer<typeof paymentCreateSchema>;

export const paymentUpdateSchema = paymentCreateSchema
  .partial()
  .extend({ version: z.number().int().min(1) });
export type PaymentUpdateDto = z.infer<typeof paymentUpdateSchema>;

export interface PaymentResponse {
  id: string;
  invoiceId: string;
  amount: string;
  currency: string;
  paymentDate: string;
  method: string;
  reference?: string;
  note?: string;
  recordedBy: { id: string; name: string };
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface InvoiceResponse extends CommercialTotalsResponse {
  id: string;
  /** Absent while the invoice is a draft. */
  number?: string;
  kind: InvoiceKind;
  correctsInvoice?: DocumentRef;
  creditNotes: Array<DocumentRef & { grandTotal: string }>;
  customer: CustomerRef;
  request?: DocumentRef;
  offer?: DocumentRef & { displayNumber: string };
  project?: { id: string; number: string; title: string };
  billing: BillingSnapshot;
  title?: string;
  introText?: string;
  invoiceDate?: string;
  dueDate?: string;
  paymentTermDays: number;
  currency: string;
  paymentTermsText?: string;
  reference?: string;
  referenceType: string;
  status: InvoiceStatus;
  paymentStatus: PaymentStatus;
  isOverdue: boolean;
  paidAmount: string;
  creditedAmount: string;
  balance: string;
  items: CommercialLineResponse[];
  payments: PaymentResponse[];
  pdfAvailable: boolean;
  qrSpecVersion?: string;
  issuedAt?: string;
  sentAt?: string;
  cancelledAt?: string;
  cancelReason?: string;
  createdBy?: { id: string; name: string };
  delivery?: {
    status: "pending" | "processing" | "done" | "failed";
    error?: string;
  };
  version: number;
  createdAt: string;
  updatedAt: string;
}
